import React, { useEffect, useCallback, useMemo, useState } from "react";
import "./viewPropertyRental.css";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
    viewPropertyRental,
    statusPropertyRental,
    updateOwnerVerified
} from "../../Redux/Actions/rentalPropertiesActions";
import MainLayout from "../../Layout/mainLayout";
import AdminMarginPopup from "./addAdminMarginPopup";
import RewardPointsPopup from "./rewardPopup";
import AssignProjectPopup from "./LinkResaleDropdown";
import UpdatePropertyMediaPopup from "./updatePropertyMediaPopup";
import UpdateFloorPlanPopup from "./updateFloorPlanPopup";
import UpdateLocalityPopup from "./updateLocalityPopup";
import { sendNotification } from "../../Redux/Actions/notificationActions";

/* ─── Helpers ─── */
const currencySymbolMap = { INR: "₹", USD: "$", AED: "د.إ", EUR: "€" };

const formatCurrency = (val, currency = "INR") => {
    if (!val && val !== 0) return "—";
    const symbol = currencySymbolMap[currency] || currencySymbolMap.INR;
    if (val >= 10000000) return `${symbol}${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `${symbol}${(val / 100000).toFixed(2)} Lacs`;
    return `${symbol}${Number(val).toLocaleString("en-IN")}`;
};

const BoolTag = ({ value }) => (
    <span className={`vrl-bool-tag vrl-bool-tag--${value ? "yes" : "no"}`}>
        {value ? "✓ Yes" : "✗ No"}
    </span>
);

const InfoItem = ({ label, value, full }) => (
    <div className={`vrl-info-item${full ? " vrl-info-item--full" : ""}`}>
        <span className="vrl-info-label">{label}</span>
        <span className="vrl-info-value">{value ?? "—"}</span>
    </div>
);

const CardSection = ({ badge, title, children }) => (
    <div className="vrl-card">
        <div className="vrl-card-header">
            <span className="vrl-card-badge">{badge}</span>
            <h2 className="vrl-card-title">{title}</h2>
        </div>
        {children}
    </div>
);

/* ── Small pill badge for verification/lock/booking-type statuses ── */
const StatusPill = ({ label, value, positive }) => (
    <span
        className={`vrl-verify-pill ${positive ? "vrl-verify-pill--on" : "vrl-verify-pill--off"}`}
        title={label}
    >
        {label}: {value}
    </span>
);

/* ─── Main Component ─── */
const ViewPropertyRental = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [adminMarginPopup, setAdminMarginPopup] = useState(false);

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");
    const [assignProjectPopup, setAssignProjectPopup] = useState(false);
    const [rewardPopup, setRewardPopup] = useState(false);
    const { propertyRentalDetail, loading } = useSelector((state) => ({
        propertyRentalDetail: state.propertyRental?.propertyRentalDetail,
        loading: state.propertyRental?.loading,
    }));

    const data = propertyRentalDetail?.data;

    useEffect(() => {
        if (!id) return;
        dispatch(viewPropertyRental(id)).catch(() => {
            toast.error("Failed to load property details.");
        });
    }, [id, dispatch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric", month: "long", day: "numeric",
        });
    }, []);

    const statusClass = (status) => {
        const map = {
            published: "vrl-status-badge--published",
            requested: "vrl-status-badge--requested",
            unpublished: "vrl-status-badge--unpublished",
            rented: "vrl-status-badge--sold",
        };
        return map[status?.toLowerCase()] || "vrl-status-badge--unpublished";
    };

    /* ── NOTE: the API returns the tenant under `user_id`, not `user`,
       and the category under `category_id`, not `category`. Both are
       populated objects (not just raw ids) despite the `_id` suffix. ── */
    const tenant = data?.user_id;
    const [mediaPopup, setMediaPopup] = useState(false);
    const [floorPlanPopup, setFloorPlanPopup] = useState(false);
    const [localityPopup, setLocalityPopup] = useState(false);
    const [ownerVerifiedLoading, setOwnerVerifiedLoading] = useState(false);

    const handleOwnerVerifiedToggle = async () => {
        if (!data) return;
        const nextValue = data.owner_verified === "verified" ? "unverified" : "verified";
        setOwnerVerifiedLoading(true);
        try {
            const res = await dispatch(
                updateOwnerVerified({ property_id: id, owner_verified: nextValue })
            );
            if (res?.statusCode === 200) {
                dispatch(viewPropertyRental(id));
            }
        } catch {
            toast.error("Failed to update owner verification");
        } finally {
            setOwnerVerifiedLoading(false);
        }
    };

    const handleStatusChange = async (e) => {
        const status = e.target.value;
        try {
            const res = await dispatch(statusPropertyRental({ id, status }));
            if (res?.statusCode === 200) {
                dispatch(viewPropertyRental(id));

                // Send notification on every status except rented
                if (status !== "rented" && tenant?._id) {
                    const statusMessages = {
                        requested: `Your property #${data?.property_id} has been marked as Requested.`,
                        unpublished: `Your property #${data?.property_id} has been Unpublished.`,
                        published: `Your property #${data?.property_id} has been Published successfully!`,
                    };

                    const statusTitles = {
                        requested: "Property Requested",
                        unpublished: "Property Unpublished",
                        published: "Property Published",
                    };

                    try {
                        await dispatch(
                            sendNotification({
                                title: statusTitles[status] || "Property Update",
                                message: statusMessages[status] || `Your property #${data?.property_id} status has been updated to ${status}.`,
                                type_name: "project",
                                type_id: tenant?._id,
                                type: "selected",
                                notification_for: "reseller",
                                list: [tenant?._id],
                                image: data?.cover_photos?.[0]?.url || "",
                                type_data: {
                                    property_id: data?.property_id,
                                    street_address: data?.street_address,
                                    category: data?.category_id?.name,
                                    rent_amount: data?.rent_amount,
                                    cover_photo: data?.cover_photos?.[0]?.url || "",
                                },
                            })
                        );
                    } catch (err) {
                        console.error("Notification failed:", err);
                    }
                }
            }
        } catch {
            toast.error("Failed to update status");
        }
    };

    const toggleAdminMarginPopup = useCallback(() => setAdminMarginPopup(prev => !prev), []);

    /* ── projectInfo — project_id is only populated when the property
       belongs to a project; standalone listings (is_standalone: true)
       carry their own top-level project_name string instead (project_id
       comes back as null), so fall back to that shape here rather than
       relying on the backend to normalize it. ── */
    const projectInfo = useMemo(() => {
        if (data?.project_id && typeof data.project_id === "object") {
            return data.project_id;
        }
        if (data?.project_name) {
            return { project_name: data.project_name, project_id: null };
        }
        return null;
    }, [data?.project_id, data?.project_name]);

    /* ── existingAdminMargin — same shape as ViewProperties. Note the
       view API doesn't return `admin_margin` at all until one has
       actually been saved via the popup, so this legitimately stays
       null for freshly created listings. ── */
    const existingAdminMargin = useMemo(() => {
        const obj = data?.admin_margin;
        if (obj && typeof obj === "object" && obj.type && obj.margin != null) {
            return {
                admin_margin_type: String(obj.type),
                admin_margin_value: String(obj.margin),
            };
        }
        return null;
    }, [data?.admin_margin]);

    /* ── Loading / Not found ── */
    if (loading) {
        return (
            <MainLayout>
                <div className="vrl-loader-wrap">
                    <div className="vrl-loader" />
                    <p className="vrl-loader-text">Loading property details…</p>
                </div>
            </MainLayout>
        );
    }

    if (!data) {
        return (
            <MainLayout>
                <div className="vrl-loader-wrap">
                    <p className="vrl-loader-text">Property not found.</p>
                </div>
            </MainLayout>
        );
    }

    /* convenience */
    const coverPhoto = data.cover_photos?.[0]?.url;
    const bedroomCount = data.no_of_bedroom ?? data.details?.summary?.bedrooms ?? 0;
    const bathCount = data.no_of_bathroom ?? data.details?.summary?.washroom ?? 0;
    const kitchenCount = data.no_of_kitchen ?? data.details?.summary?.kitchen ?? 0;

    const safetyFeatures = Array.isArray(data.safety_features) ? data.safety_features : [];
    const addOnRooms = Array.isArray(data.additional_rooms) ? data.additional_rooms : [];
    const plotAmenities = Array.isArray(data.amenities) ? data.amenities : [];
    const vibes = Array.isArray(data.property_vibes) ? data.property_vibes : [];
    const faqs = Array.isArray(data.faqs) ? data.faqs : [];
    const documents = Array.isArray(data.documents) ? data.documents : [];
    const furnishedAmenities = Array.isArray(data.furnished_amenities) ? data.furnished_amenities : [];
    const semiFurnishedAmenities = Array.isArray(data.semi_furnished_amenities) ? data.semi_furnished_amenities : [];
    const unfurnishedAmenities = Array.isArray(data.unfurnished_amenities) ? data.unfurnished_amenities : [];
    const leaseTypes = Array.isArray(data.lease_type) ? data.lease_type : [];
    const threeSixty = Array.isArray(data.three_sixty) ? data.three_sixty : [];
    const propertyAR = Array.isArray(data.property_ar) ? data.property_ar : [];
    const rentIncludes = Array.isArray(data.rent_includes) ? data.rent_includes : [];
    const habitatItems = Array.isArray(data.habitat) ? data.habitat : [];
    const collectionsList = Array.isArray(data.collections) ? data.collections : [];
    const commercialFacilities = Array.isArray(data.commercial_facilities) ? data.commercial_facilities : [];

    return (
        <MainLayout>
            <div className="vrl-wrapper">

                {/* ── Header ── */}
                <div className="vrl-header">
                    <div className="vrl-header-left">
                        <Link to="/rental_property" className="vrl-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vrl-header-divider" />
                        <div>
                            <div className="vrl-breadcrumb">Properties / Rental / View</div>
                            <h1 className="vrl-title">Property Details</h1>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        {/* Dooritt Verified — read-only, set automatically on VR upload */}
                        <div className="vrl-owner-verified-toggle-wrap">
                            <span className="vrl-owner-verified-label">Dooritt Verified</span>
                            <span
                                className={`vrl-static-pill ${data.dooritt_verified === "verified" ? "vrl-static-pill--on" : "vrl-static-pill--off"}`}
                            >
                                {data.dooritt_verified === "verified" ? "✓ Verified" : "Unverified"}
                            </span>
                        </div>

                        {/* Owner Verified — manual toggle */}
                        <div className="vrl-owner-verified-toggle-wrap">
                            <span className="vrl-owner-verified-label">Owner Verified</span>
                            <button
                                type="button"
                                className={`vrl-toggle-switch ${data.owner_verified === "verified" ? "vrl-toggle-switch--on" : ""}`}
                                onClick={handleOwnerVerifiedToggle}
                                disabled={ownerVerifiedLoading}
                                aria-pressed={data.owner_verified === "verified"}
                            >
                                <span className="vrl-toggle-knob" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Hero ── */}
                <div className="vrl-hero-card">
                    {coverPhoto
                        ? <img src={coverPhoto} alt="cover" className="vrl-hero-cover" />
                        : <div className="vrl-hero-cover-placeholder" />
                    }
                    <div className="vrl-hero-overlay">
                        <div className="vrl-hero-badges">
                            <span className={`vrl-status-badge ${statusClass(data.status)}`}>
                                {data.status || "Unknown"}
                            </span>
                            {data.project_type?.name && (
                                <span className="vrl-type-badge">{data.project_type.name}</span>
                            )}
                            {data.category_id?.name && (
                                <span className="vrl-type-badge">{data.category_id.name}</span>
                            )}
                            {data.type && (
                                <span className="vrl-type-badge" style={{ textTransform: "capitalize" }}>
                                    {data.type}
                                </span>
                            )}
                            {data.lock_status && (
                                <span className="vrl-type-badge" style={{ textTransform: "capitalize" }}>
                                    🔒 {data.lock_status}
                                </span>
                            )}
                            {data.booking_status && (
                                <span className="vrl-type-badge" style={{ textTransform: "capitalize" }}>
                                    {data.booking_status.replace(/_/g, " ")}
                                </span>
                            )}
                        </div>
                        <h2 className="vrl-hero-name">
                            {data.city_id?.city_name
                                ? `${data.category_id?.name || "Property"} — ${data.city_id.city_name}`
                                : `Property #${data.property_id}`}
                        </h2>
                        <div className="vrl-hero-meta">
                            {data.street_address && (
                                <span className="vrl-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    {data.street_address}
                                </span>
                            )}
                            {data.availability_status && (
                                <span className="vrl-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    {data.availability_status.replace(/_/g, " ")}
                                </span>
                            )}
                            {data.property_id && (
                                <span className="vrl-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    ID: {data.property_id}
                                </span>
                            )}
                        </div>
                        <div style={{ marginTop: 12, display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {data.business_type === "resale" &&
                                <button
                                    onClick={() => setAssignProjectPopup(true)}
                                    style={{
                                        padding: "8px 14px", borderRadius: 10,
                                        border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.15)",
                                        color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer"
                                    }}
                                >
                                    {projectInfo ? "🔄 Assign Project" : "+ Assign Project"}
                                </button>
                            }
                            <button
                                onClick={() => setRewardPopup(true)}
                                style={{
                                    padding: "8px 14px", borderRadius: 10,
                                    border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.15)",
                                    color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer"
                                }}
                            >
                                🎯 Reward Points
                            </button>
                            <button
                                onClick={() => setMediaPopup(true)}
                                style={{
                                    padding: "8px 14px", borderRadius: 10,
                                    border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.15)",
                                    color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer"
                                }}
                            >
                                🖼️ Edit Media
                            </button>

                            <button
                                onClick={() => setFloorPlanPopup(true)}
                                style={{
                                    padding: "8px 14px", borderRadius: 10,
                                    border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.15)",
                                    color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer"
                                }}
                            >
                                🏗️ Floor Plan
                            </button>
                        </div>

                        <button
                            onClick={() => setLocalityPopup(true)}
                            style={{
                                padding: "8px 14px", borderRadius: 10,
                                border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.15)",
                                color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer"
                            }}
                        >
                            📍 Localities {data.localities?.length ? `(${data.localities.length})` : ""}
                        </button>
                    </div>
                </div>

                {/* ── Stats Row ── */}
                <div className="vrl-stats-row">
                    <div className="vrl-stat-card">
                        <span className="vrl-stat-label">Rent Amount</span>
                        <span className="vrl-stat-value">
                            {formatCurrency(data.rent_amount, data.currency || "INR")}
                        </span>
                        <span className="vrl-stat-sub">
                            {data.rent_frequency ? `per ${data.rent_frequency}` : "Base rent"}
                        </span>
                    </div>
                    <div className="vrl-stat-card">
                        <span className="vrl-stat-label">Super Area</span>
                        <span className="vrl-stat-value">
                            {data.super_area ? `${data.super_area}` : "—"}
                        </span>
                        <span className="vrl-stat-sub">{data.super_area_measure_in || "sq.ft"}</span>
                    </div>
                    <div className="vrl-stat-card">
                        <span className="vrl-stat-label">Config</span>
                        <span className="vrl-stat-value">
                            {bedroomCount ? `${bedroomCount} BHK` : "—"}
                        </span>
                        <span className="vrl-stat-sub">{bathCount} Bath · {kitchenCount} Kitchen</span>
                    </div>
                    <div className="vrl-stat-card">
                        <span className="vrl-stat-label">Status</span>
                        <select
                            value={data.status || ""}
                            onChange={handleStatusChange}
                            className="vrl-status-select"
                        >
                            <option value="requested">Requested</option>
                            <option value="unpublished">Unpublished</option>
                            <option value="published">Published</option>
                            <option value="rented">Rented</option>
                        </select>
                        <span className="vrl-stat-sub">Change status</span>
                    </div>
                </div>

                {/* ── Two-column Grid ── */}
                <div className="vrl-grid-layout">

                    {/* ── Left Column ── */}
                    <div className="vrl-col">

                        {/* Basic Info */}
                        <CardSection badge="01" title="Basic Info">
                            <div className="vrl-info-grid">
                                <InfoItem label="Property ID" value={data.property_id} />
                                <InfoItem label="Business Type" value={data.business_type ? data.business_type.charAt(0).toUpperCase() + data.business_type.slice(1) : "—"} />
                                <InfoItem label="Listing As" value={data.listing_as ? data.listing_as.charAt(0).toUpperCase() + data.listing_as.slice(1) : "—"} />
                                <InfoItem label="Category" value={data.category_id?.name} />
                                <InfoItem label="Type" value={data.type} />
                                <InfoItem label="Project Name" value={projectInfo?.project_name} />
                                <InfoItem label="Project ID" value={projectInfo?.project_id} />
                                <InfoItem label="Availability Status" value={data.availability_status?.replace(/_/g, " ")} />
                                <InfoItem label="Available From" value={formatDate(data.available_from)} />
                                <InfoItem label="Lock Status" value={data.lock_status ? data.lock_status.charAt(0).toUpperCase() + data.lock_status.slice(1) : "—"} />
                                <InfoItem label="Booking Status" value={data.booking_status ? data.booking_status.replace(/_/g, " ") : "—"} />
                                <InfoItem label="Created At" value={formatDate(data.created_at)} />
                                <InfoItem label="Updated At" value={formatDate(data.updated_at)} />
                            </div>
                        </CardSection>

                        {/* Location */}
                        <CardSection badge="02" title="Location">
                            <div className="vrl-info-grid">
                                <InfoItem label="City" value={data.city_id?.city_name} />
                                <InfoItem label="State" value={data.state_id?.state_name} />
                                <InfoItem label="Country" value={data.country_id?.country_name} />
                                <InfoItem label="Zipcode" value={data.zipcode || "—"} />
                                <InfoItem label="House Number" value={data.house_number || "—"} />
                                <div className="vrl-info-item vrl-info-item--full">
                                    <span className="vrl-info-label">Localities</span>
                                    {Array.isArray(data.localities) && data.localities.length > 0 ? (
                                        <div className="vrl-locality-list" style={{ marginTop: 4 }}>
                                            {data.localities.map((l) => (
                                                <span key={l._id} className="vrl-locality-tag">
                                                    {l.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="vrl-info-value">—</span>
                                    )}
                                </div>
                                <InfoItem label="Street Address" value={data.street_address} full />
                            </div>
                            {data.location?.coordinates?.length === 2 && (
                                <div className="vrl-map-embed-wrap">
                                    <iframe
                                        title="Property Location"
                                        width="100%"
                                        height="220"
                                        style={{ border: 0, borderRadius: "var(--vrl-radius-lg)", display: "block" }}
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps?q=${data.location.coordinates[1]},${data.location.coordinates[0]}&z=15&output=embed`}
                                    />
                                </div>
                            )}
                        </CardSection>

                        {/* Area & Pricing */}
                        <CardSection badge="03" title="Area & Pricing">
                            <div className="vrl-info-grid">
                                <InfoItem label="Super Area" value={data.super_area ? `${data.super_area} ${data.super_area_measure_in || "sq.ft"}` : "—"} />
                                <InfoItem label="Carpet Area" value={data.carpet_area ? `${data.carpet_area} ${data.carpet_area_measure_in || "sq.ft"}` : "—"} />
                                <InfoItem label="Rent Amount" value={formatCurrency(data.rent_amount, data.currency)} />
                                <InfoItem label="Rent Frequency" value={data.rent_frequency ? data.rent_frequency.charAt(0).toUpperCase() + data.rent_frequency.slice(1) : "—"} />
                                <InfoItem label="Security Deposit" value={formatCurrency(data.security, data.currency)} />
                                <InfoItem label="Convenience Fee" value={formatCurrency(data.convinienceFee, data.currency)} />
                                <InfoItem label="Maintenance" value={data.maintainence_value ? formatCurrency(data.maintainence_value, data.currency) : "—"} />
                                <InfoItem label="Rent Negotiable" value={<BoolTag value={data.is_rent_negotiable} />} />
                                <InfoItem label="Agent Margin" value={data.margin_ratio?.agent_margin != null ? `${data.margin_ratio.agent_margin}%` : "—"} />
                                <InfoItem label="Admin Margin (Ratio)" value={data.margin_ratio?.admin_margin != null ? `${data.margin_ratio.admin_margin}%` : "—"} />
                            </div>
                        </CardSection>

                        {/* Rooms & Config */}
                        <CardSection badge="04" title="Rooms & Configuration">
                            <div className="vrl-info-grid">
                                <InfoItem label="Bedrooms" value={bedroomCount || "—"} />
                                <InfoItem label="Bathrooms" value={bathCount || "—"} />
                                <InfoItem label="Kitchens" value={kitchenCount || "—"} />
                                <InfoItem label="Open Parking" value={data.open_parking ?? "—"} />
                                <InfoItem label="Cover Parking" value={data.cover_parking ?? "—"} />
                                <InfoItem label="No. of Lifts" value={data.no_of_lifts ?? "—"} />
                                <InfoItem label="Maintenance" value={<BoolTag value={data.maintainence} />} />
                                <InfoItem label="Pre-lease" value={<BoolTag value={data.is_pre_lease} />} />
                            </div>

                            {data.details?.summary && (
                                <div className="vrl-subsection">
                                    <span className="vrl-info-label vrl-subsection-label">
                                        Room Summary
                                    </span>
                                    <div className="vrl-amenity-list">
                                        {Object.entries(data.details.summary).map(([key, val]) => (
                                            <span key={key} className="vrl-amenity-tag">
                                                {key.replace(/([A-Z])/g, " $1").trim()}: <strong style={{ marginLeft: 4 }}>{val}</strong>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.details?.grouped?.bedrooms?.length > 0 && (
                                <div className="vrl-subsection">
                                    <span className="vrl-info-label vrl-subsection-label">
                                        Bedroom Details
                                    </span>
                                    <div className="vrl-spec-list">
                                        {data.details.grouped.bedrooms.map((br, i) => (
                                            <div key={i} className="vrl-faq-item">
                                                <p className="vrl-faq-q">Bedroom {i + 1}</p>
                                                <div className="vrl-spec-tags">
                                                    {br.attachedBathroom > 0 && (
                                                        <span className="vrl-spec-tag vrl-spec-tag--verified">✓ Attached Bathroom</span>
                                                    )}
                                                    {br.attachedBalcony > 0 && (
                                                        <span className="vrl-spec-tag vrl-spec-tag--verified">✓ Attached Balcony</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardSection>

                        {/* Additional Rooms */}
                        {addOnRooms.length > 0 && (
                            <CardSection badge="05" title="Additional Rooms">
                                <div className="vrl-locality-list">
                                    {addOnRooms.map((room, i) => (
                                        <span key={i} className="vrl-locality-tag" style={{ textTransform: "capitalize" }}>
                                            {room}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Rent Includes */}
                        {rentIncludes.length > 0 && (
                            <CardSection badge="05B" title="Rent Includes">
                                <div className="vrl-amenity-list">
                                    {rentIncludes.map((item, i) => (
                                        <span key={i} className="vrl-amenity-tag">
                                            {item.icon && (
                                                <img src={item.icon} alt={item.item_name} className="vrl-amenity-icon" />
                                            )}
                                            {item.item_name}
                                            {item.room_category && (
                                                <span style={{ color: "var(--vrl-text-muted)", marginLeft: 4, textTransform: "capitalize" }}>
                                                    ({item.room_category.replace(/_/g, " ")})
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* About Property */}
                        {data.about_property && (
                            <CardSection badge="06A" title="About Property">
                                <p className="vrl-description">{data.about_property}</p>
                            </CardSection>
                        )}

                        {/* Space & Other Info */}
                        {(data?.spaceInfo || data?.otherInfo) && (
                            <CardSection badge="06B" title="Space & Other Info">

                                {data?.spaceInfo && (
                                    <>
                                        <p className="vrl-info-label vrl-subsection-label">Space Info</p>
                                        <div className="vrl-info-grid">
                                            <InfoItem label="Super Area" value={data.spaceInfo.super_area ? `${data.spaceInfo.super_area} ${data.spaceInfo.super_area_measure_in || "sq.ft"}` : "—"} />
                                            <InfoItem label="Carpet Area" value={data.spaceInfo.carpet_area ? `${data.spaceInfo.carpet_area} ${data.spaceInfo.carpet_area_measure_in || "sq.ft"}` : "—"} />
                                            <InfoItem label="Lock-in Period" value={data.spaceInfo.lock_in_period ? `${data.spaceInfo.lock_in_period} ${data.spaceInfo.lock_in_period_measure_in || ""}` : "—"} />
                                            <InfoItem label="Lifts" value={data.spaceInfo.lifts ?? "—"} />
                                        </div>
                                    </>
                                )}

                                {data?.spaceInfo && data?.otherInfo && (
                                    <div className="vrl-divider vrl-divider--spaced" />
                                )}

                                {data?.otherInfo && (
                                    <>
                                        <p className="vrl-info-label vrl-subsection-label">Other Info</p>
                                        <div className="vrl-info-grid">
                                            <InfoItem label="Property Type" value={data.otherInfo.propertyType} />
                                            <InfoItem label="Age of Property" value={data.otherInfo.ageOfProperty} />
                                            <InfoItem label="Facing" value={data.otherInfo.facing?.length ? data.otherInfo.facing.join(", ") : "—"} />
                                            <InfoItem label="Availability Status" value={data.otherInfo.availabilityStatus?.replace(/_/g, " ")} />
                                            <InfoItem label="Available From" value={formatDate(data.otherInfo.availableFrom)} />
                                        </div>

                                        <div className="vrl-subsection">
                                            <span className="vrl-info-label vrl-subsection-label">Suitability</span>
                                            <div className="vrl-info-grid">
                                                <InfoItem label="Party Friendly" value={<BoolTag value={data.otherInfo.isPartyFriendly} />} />
                                                <InfoItem label="Pet Friendly" value={<BoolTag value={data.otherInfo.isPetFriendly} />} />
                                                <InfoItem label="Owner Free" value={<BoolTag value={data.otherInfo.isOwnerFree} />} />
                                                <InfoItem label="Student Friendly" value={<BoolTag value={data.otherInfo.isStudentFriendly} />} />
                                                <InfoItem label="Couple Friendly" value={<BoolTag value={data.otherInfo.isCoupleFriendly} />} />
                                                <InfoItem label="Working Professional Friendly" value={<BoolTag value={data.otherInfo.isWorkingProfessionalFriendly} />} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </CardSection>
                        )}

                        {/* Safety Features */}
                        {safetyFeatures.length > 0 && (
                            <CardSection badge="17" title="Safety Features">
                                <div className="vrl-spec-list">
                                    {safetyFeatures.map((sf, i) => (
                                        <span key={i} className="vrl-spec-tag vrl-spec-tag--verified vrl-spec-tag--inline">
                                            🛡 {sf}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Habitat — Prime Location Overview */}
                        {habitatItems.length > 0 && (
                            <CardSection badge="06C" title="Prime Location Overview">
                                <div className="vrl-amenity-list">
                                    {habitatItems.map((h, i) => (
                                        <span key={i} className="vrl-amenity-tag">
                                            {h.name}
                                            {h.distance_km != null && (
                                                <span style={{ color: "var(--vrl-text-muted)", marginLeft: 4 }}>
                                                    ({h.distance_km} km)
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Collections */}
                        {collectionsList.length > 0 && (
                            <CardSection badge="06D" title="Collections">
                                <div className="vrl-amenity-list">
                                    {collectionsList.map((c, i) => (
                                        <span key={i} className="vrl-amenity-tag">
                                            {c.image && <img src={c.image} alt={c.name} className="vrl-amenity-icon" />}
                                            {c.name || c}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Commercial Facilities */}
                        {commercialFacilities.length > 0 && (
                            <CardSection badge="06E" title="Commercial Facilities">
                                <div className="vrl-locality-list">
                                    {commercialFacilities.map((cf, i) => (
                                        <span key={i} className="vrl-locality-tag">
                                            {cf.name || cf}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}
                    </div>

                    {/* ── Right Column ── */}
                    <div className="vrl-col">

                        <CardSection badge="07" title="Owner Details">
                            {tenant ? (
                                <div className="vrl-info-grid">
                                    <InfoItem label="Name" value={tenant?.name} />
                                    <InfoItem label="ID" value={tenant?.staff_id} />
                                    <InfoItem label="Email" value={tenant?.email} />
                                    <InfoItem label="Contact" value={
                                        tenant?.contact_number
                                            ? `${tenant?.country_code || ""} ${tenant?.contact_number}`
                                            : "—"
                                    } />
                                </div>
                            ) : (
                                <div className="vrl-empty-note">No tenant information available.</div>
                            )}
                        </CardSection>

                        {/* ── Assigned Project & Rewards ── */}
                        <CardSection badge="8" title="Assigned Project & Rewards">
                            <p className="vrl-info-label vrl-subsection-label">
                                Assigned Project
                            </p>
                            {projectInfo ? (
                                <div className="vrl-info-grid">
                                    <InfoItem label="Project Name" value={projectInfo?.project_name} />
                                    <InfoItem label="Project ID" value={projectInfo?.project_id} />
                                </div>
                            ) : (
                                <div className="vrl-empty-note">No project assigned yet.</div>
                            )}

                            <div className="vrl-divider vrl-divider--spaced" />

                            <p className="vrl-info-label vrl-subsection-label">
                                Reward Points
                            </p>
                            {data?.max_reward_points || data?.min_reward_points ? (
                                <div className="vrl-info-grid">
                                    <InfoItem label="Min Reward Points" value={data.min_reward_points} />
                                    <InfoItem label="Max Reward Points" value={data.max_reward_points} />
                                </div>
                            ) : (
                                <div className="vrl-empty-note">No reward points configured.</div>
                            )}
                        </CardSection>

                        {/* Cover Photo */}
                        {coverPhoto && (
                            <CardSection badge="9" title="Cover Photo">
                                <div className="vrl-cover-wrap">
                                    <img src={coverPhoto} alt="cover" className="vrl-cover-img" />
                                </div>
                            </CardSection>
                        )}

                        {/* Floor Plan & Electricity Bill */}
                        {(data.floor_plan_image || data.electricity_bill || canEdit) && (
                            <CardSection badge="9B" title="Floor Plan & Documents">
                                <div className="vrl-margin-card-header">
                                    <div className="vrl-margin-card-title-row">
                                        <span className="vrl-margin-card-icon">🏗️</span>
                                        <p className="vrl-margin-card-title">
                                            {data.floor_plan_label || "Floor Plan"}
                                        </p>
                                    </div>
                                    {canEdit && (
                                        <button
                                            className="vrl-margin-edit-btn"
                                            onClick={() => setFloorPlanPopup(true)}
                                        >
                                            {data.floor_plan_image ? "✏️ Edit" : "+ Add"}
                                        </button>
                                    )}
                                </div>

                                {data.floor_plan_image ? (
                                    <div className="vrl-cover-wrap" style={{ marginBottom: 16 }}>
                                        <img
                                            src={data.floor_plan_image}
                                            alt={data.floor_plan_label || "Floor plan"}
                                            className="vrl-cover-img"
                                            style={{ objectFit: "contain", background: "var(--vrl-surface-2)" }}
                                        />
                                    </div>
                                ) : (
                                    <div className="vrl-margin-empty">
                                        <span>—</span>
                                        <span>No floor plan uploaded</span>
                                    </div>
                                )}

                                {data.electricity_bill && (
                                    <>
                                        <div className="vrl-divider vrl-divider--spaced" />
                                        <p className="vrl-info-label vrl-subsection-label">Electricity Bill</p>
                                        <div className="vrl-yt-list">
                                            <a
                                                href={data.electricity_bill}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="vrl-brochure-btn"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" />
                                                </svg>
                                                View Electricity Bill
                                            </a>
                                        </div>
                                    </>
                                )}
                            </CardSection>
                        )}

                        {/* All Images (full-size + thumbnails, if present) */}
                        {data.images?.length > 0 && (
                            <CardSection badge="10" title={`Property Images (${data.images.length})`}>
                                <div className="vrl-images-grid">
                                    {data.images.map((img, i) => (
                                        <img
                                            key={i}
                                            src={img.url || img}
                                            alt={`img-${i}`}
                                            className="vrl-thumb"
                                        />
                                    ))}
                                </div>

                                {data.images_thumbnail?.length > 0 && (
                                    <div className="vrl-subsection">
                                        <span className="vrl-info-label vrl-subsection-label">Thumbnails</span>
                                        <div className="vrl-images-grid">
                                            {data.images_thumbnail.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img.url || img}
                                                    alt={`img-thumb-${i}`}
                                                    className="vrl-thumb"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardSection>
                        )}

                        {/* Property Vibes */}
                        {vibes.length > 0 && (
                            <CardSection badge="11" title="Property Vibes">
                                <div className="vrl-amenity-list">
                                    {vibes.map((v, i) => (
                                        <span key={i} className="vrl-amenity-tag">
                                            {v.icon && (
                                                <img src={v.icon} alt={v.name} className="vrl-amenity-icon" />
                                            )}
                                            {v.name}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Plot Amenities */}
                        {plotAmenities.length > 0 && (
                            <CardSection badge="12" title={`Amenities (${plotAmenities.length})`}>
                                <div className="vrl-amenity-list">
                                    {plotAmenities.map((a, i) => (
                                        <span key={i} className="vrl-amenity-tag">
                                            {a.image && (
                                                <img src={a.image} alt={a.name} className="vrl-amenity-icon" />
                                            )}
                                            {a.name || a}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Furnished Inventory */}
                        <CardSection badge="13" title="Furnished Inventory">
                            <div className="vrl-info-grid">
                                <InfoItem
                                    label="Furnished Status"
                                    value={data.furnished_status ? data.furnished_status.charAt(0).toUpperCase() + data.furnished_status.slice(1) : "—"}
                                />
                            </div>

                            {furnishedAmenities.length > 0 && (
                                <div className="vrl-subsection">
                                    <span className="vrl-info-label vrl-subsection-label">Furnished Amenities</span>
                                    <div className="vrl-amenity-list">
                                        {furnishedAmenities.map((a, i) => (
                                            <span key={i} className="vrl-amenity-tag">{a.name || a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {semiFurnishedAmenities.length > 0 && (
                                <div className="vrl-subsection">
                                    <span className="vrl-info-label vrl-subsection-label">Semi-furnished Amenities</span>
                                    <div className="vrl-amenity-list">
                                        {semiFurnishedAmenities.map((a, i) => (
                                            <span key={i} className="vrl-amenity-tag">{a.name || a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {unfurnishedAmenities.length > 0 && (
                                <div className="vrl-subsection">
                                    <span className="vrl-info-label vrl-subsection-label">Unfurnished Amenities</span>
                                    <div className="vrl-amenity-list">
                                        {unfurnishedAmenities.map((a, i) => (
                                            <span key={i} className="vrl-amenity-tag">{a.name || a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!furnishedAmenities.length && !semiFurnishedAmenities.length && !unfurnishedAmenities.length && (
                                <div className="vrl-empty-note">No furnishing amenities listed.</div>
                            )}
                        </CardSection>

                        {/* Rental & Lease Info */}
                        <CardSection badge="14" title="Rental & Lease Info">
                            <div className="vrl-info-grid">
                                <InfoItem label="Rental Details" value={data.rental_details || "—"} />
                                <InfoItem label="Leased To" value={data.leased_to || "—"} />
                                <InfoItem label="Lease Type" value={leaseTypes.length ? leaseTypes.join(", ") : "—"} />
                                <InfoItem label="Lock-in Period" value={data.lock_in_period ? `${data.lock_in_period} months` : "—"} />
                                <InfoItem label="Lock-in Months" value={data.lock_in_months ?? "—"} />
                                <InfoItem label="360° Media" value={threeSixty.length ? `${threeSixty.length} item(s)` : "—"} />
                            </div>
                        </CardSection>

                        {/* 360° VR Tours */}
                        {threeSixty.length > 0 && (
                            <CardSection badge="14C" title={`360° VR Tours (${threeSixty.length})`}>
                                <div className="vrl-vr-list">
                                    {threeSixty.map((vr, i) => (
                                        <div key={vr._id || i} className="vrl-vr-item">
                                            <div className="vrl-vr-item-header">
                                                <span className="vrl-vr-config-badge">
                                                    {vr.configuration || `Item ${i + 1}`}
                                                </span>
                                                {vr.is_user_vr && (
                                                    <span className="vrl-vr-source-tag">User Uploaded</span>
                                                )}
                                            </div>

                                            {vr.iframe_url ? (
                                                <div className="vrl-vr-embed-wrap">
                                                    <iframe
                                                        title={`VR Tour — ${vr.configuration || i + 1}`}
                                                        src={vr.iframe_url}
                                                        allow="xr-spatial-tracking; gyroscope; accelerometer"
                                                        allowFullScreen
                                                        loading="lazy"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="vrl-empty-note">No VR URL available for this item.</div>
                                            )}
                                            <a

                                                href={vr.iframe_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="vrl-brochure-btn"
                                                style={{ marginTop: 10 }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                                    <polyline points="15 3 21 3 21 9" />
                                                    <line x1="10" y1="14" x2="21" y2="3" />
                                                </svg>
                                                Open in New Tab
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* AR Items */}
                        {propertyAR.length > 0 && (
                            <CardSection badge="14B" title={`AR Content (${propertyAR.length})`}>
                                <div className="vrl-faq-list">
                                    {propertyAR.map((ar, i) => (
                                        <div key={ar._id || i} className="vrl-faq-item">
                                            <p className="vrl-faq-q" style={{ textTransform: "capitalize" }}>
                                                {ar.type || "AR Item"} #{i + 1}
                                            </p>
                                            {Array.isArray(ar.type_data) && ar.type_data.map((td, j) => (
                                                <p key={j} className="vrl-faq-a">
                                                    {td.iframe_url && (
                                                        <a href={td.iframe_url} target="_blank" rel="noreferrer" style={{ color: "var(--vrl-accent)" }}>
                                                            {td.iframe_url}
                                                        </a>
                                                    )}
                                                    {td.video && (
                                                        <a href={td.video} target="_blank" rel="noreferrer" style={{ color: "var(--vrl-accent)", marginLeft: td.iframe_url ? 8 : 0 }}>
                                                            {td.video}
                                                        </a>
                                                    )}
                                                </p>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Documents */}
                        {documents.length > 0 && (
                            <CardSection badge="15" title={`Documents (${documents.length})`}>
                                <div className="vrl-yt-list">
                                    {documents.map((doc, i) => (
                                        <a
                                            key={i}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="vrl-brochure-btn"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="12" y1="18" x2="12" y2="12" />
                                                <line x1="9" y1="15" x2="15" y2="15" />
                                            </svg>
                                            {doc.name?.split("_").slice(2).join("_") || `Document ${i + 1}`}
                                        </a>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* ── Admin Margin ── */}
                        {canAdd && canEdit && (
                            <CardSection badge="16" title="Admin Margin">
                                <div className="vrl-margin-card-header">
                                    <div className="vrl-margin-card-title-row">
                                        <span className="vrl-margin-card-icon">💰</span>
                                        <p className="vrl-margin-card-title">Admin Margin</p>
                                    </div>
                                    <button
                                        className="vrl-margin-edit-btn"
                                        onClick={toggleAdminMarginPopup}
                                    >
                                        {existingAdminMargin ? "✏️ Edit" : "+ Add"}
                                    </button>
                                </div>

                                {existingAdminMargin ? (
                                    <div className="vrl-margin-section">
                                        <div className="vrl-margin-data-item">
                                            <span className="vrl-margin-data-label">Type</span>
                                            <span className="vrl-margin-type-badge">
                                                {existingAdminMargin.admin_margin_type === "percentage" && "% Percentage"}
                                                {existingAdminMargin.admin_margin_type === "flat" && "₹ Flat"}
                                                {existingAdminMargin.admin_margin_type === "per_sq_ft" && "□ Per Sq.ft"}
                                                {!["percentage", "flat", "per_sq_ft"].includes(existingAdminMargin.admin_margin_type) && existingAdminMargin.admin_margin_type}
                                            </span>
                                        </div>
                                        <div className="vrl-margin-data-item">
                                            <span className="vrl-margin-data-label">Value</span>
                                            <span className="vrl-margin-data-value">
                                                {existingAdminMargin.admin_margin_type === "percentage"
                                                    ? `${existingAdminMargin.admin_margin_value}%`
                                                    : `₹ ${Number(existingAdminMargin.admin_margin_value).toLocaleString("en-IN")}`}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="vrl-margin-empty">
                                        <span>—</span>
                                        <span>No admin margin configured</span>
                                    </div>
                                )}
                            </CardSection>
                        )}

                        {/* FAQs */}
                        {faqs.length > 0 && (
                            <CardSection badge="18" title={`FAQs (${faqs.length})`}>
                                <div className="vrl-faq-list">
                                    {faqs.map((faq, i) => (
                                        <div key={i} className="vrl-faq-item">
                                            <p className="vrl-faq-q">Q: {faq.question}</p>
                                            <p className="vrl-faq-a">A: {faq.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Admin Margin Popup ── */}
            <AdminMarginPopup
                isOpen={adminMarginPopup}
                onClose={() => {
                    dispatch(viewPropertyRental(id));
                    setAdminMarginPopup(false);
                }}
                propertyId={id}
                existingData={existingAdminMargin}
            />

            <AssignProjectPopup
                isOpen={assignProjectPopup}
                onClose={() => setAssignProjectPopup(false)}
                propertyId={id}
                currentProject={projectInfo}
                onAssigned={async () => {
                    if (tenant?._id) {
                        try {
                            await dispatch(
                                sendNotification({
                                    title: "Project Assigned",
                                    message: `A project has been assigned to your property #${data?.property_id}`,
                                    type_name: "project",
                                    type_id: tenant?._id,
                                    type: "selected",
                                    notification_for: "reseller",
                                    list: [tenant?._id],
                                    image: data?.cover_photos?.[0]?.url || "",
                                    type_data: {
                                        property_id: data?.property_id,
                                        property_location: data?.street_address,
                                        category: data?.category_id?.name,
                                        rent_amount: data?.rent_amount,
                                        cover_photo: data?.cover_photos?.[0]?.url || "",
                                    },
                                })
                            );
                        } catch (err) {
                            console.error("Notification failed:", err);
                        }
                    }

                    setAssignProjectPopup(false);
                }}
            />
            <RewardPointsPopup
                isOpen={rewardPopup}
                onClose={() => {
                    dispatch(viewPropertyRental(id));
                    setRewardPopup(false);
                }}
                propertyId={id}
            />

            <UpdatePropertyMediaPopup
                isOpen={mediaPopup}
                onClose={() => {
                    dispatch(viewPropertyRental(id));
                    setMediaPopup(false);
                }}
                propertyId={id}
                existingData={{
                    cover_photos: data?.cover_photos,
                    images: data?.images,
                    cover_photos_thumbnail: data?.cover_photos_thumbnail,
                    images_thumbnail: data?.images_thumbnail,
                }}
            />

            <UpdateFloorPlanPopup
                isOpen={floorPlanPopup}
                onClose={() => {
                    dispatch(viewPropertyRental(id));
                    setFloorPlanPopup(false);
                }}
                propertyId={id}
                existingData={{
                    floor_plan_image: data?.floor_plan_image,
                    floor_plan_label: data?.floor_plan_label,
                }}
            />

            <UpdateLocalityPopup
                isOpen={localityPopup}
                onClose={() => {
                    dispatch(viewPropertyRental(id));
                    setLocalityPopup(false);
                }}
                propertyId={id}
                existingLocalities={data?.localities || []}
            />

        </MainLayout >
    );
};

export default ViewPropertyRental;